/**
 * High-level browser API wrapper for `/api/portal/*`.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.1.2 (Phase 1 foundation).
 * This is the surface that migrated pages (Dashboard / Vehicles /
 * Shipments / Invoices / Documents / Recommendations / Support /
 * AccountSettings) call. It is a thin layer on top of `portalApi()`
 * in `apiClient.ts`, exposing the `apiGet / apiPost / apiPatch /
 * apiDelete` shape used by the legacy PSM portal/ pages so the
 * Phase 2-4 page-migration PRs can copy-adapt with minimal diffing.
 *
 * Why layer on top of portalApi?
 *   - portalApi already handles the customer-portal CSRF model
 *     (cookie name `portal_csrf`, header `X-CSRF-Token`, see
 *     PORTAL_DEPLOY_SSOT §8.1).
 *   - Reusing it keeps a single error-mapping path (PortalApiError)
 *     and avoids drift between the two clients.
 *
 * Differences vs PSM portal's lib/portalApi.ts:
 *   - CSRF cookie is `portal_csrf` (PSM uses `csrf_token`). The Phase 2-4
 *     pages should NOT depend on the cookie name directly — they only
 *     call these helpers.
 *   - 401 redirects to `/login` with a `?redirect=` hint so post-login
 *     lands back on the original page. Pages that wrap themselves with
 *     `RequireAuth` already get this redirect; the redirect here is the
 *     safety net for direct API calls that bypass the route guard.
 *   - 503 surfaces as `PortalFeatureDisabledError` so the UI can render
 *     a "feature disabled" empty state without retrying.
 */
import { PortalApiError, portalApi } from './apiClient';

/** 503 from the BFF — the requested portal feature is disabled (env flag
 *  off, or the upstream PSM endpoint is unavailable in this env). UI
 *  should render an explanatory empty state, not retry. */
export class PortalFeatureDisabledError extends Error {
  constructor(public readonly code: string) {
    super(`Portal feature disabled: ${code}`);
    this.name = 'PortalFeatureDisabledError';
  }
}

function redirectToLogin(): void {
  // Guard against SSR / test environments without `window`.
  if (typeof window === 'undefined') return;
  const here = window.location.pathname + window.location.search;
  // Already on /login? do not loop.
  if (window.location.pathname.startsWith('/login')) return;
  const redirect = encodeURIComponent(here);
  window.location.assign(`/login?redirect=${redirect}`);
}

/** Map portalApi errors into the contract this module exposes:
 *  - 401  → side-effect: redirect to /login, then re-throw the original
 *           PortalApiError (callers can short-circuit on .status).
 *  - 503  → PortalFeatureDisabledError (no retry).
 *  - rest → pass through. */
function rethrow(err: unknown): never {
  if (err instanceof PortalApiError) {
    if (err.status === 401) {
      redirectToLogin();
      throw err;
    }
    if (err.status === 503) {
      throw new PortalFeatureDisabledError(err.code);
    }
  }
  throw err;
}

/** GET `/api/portal/<path>` and parse JSON. The `path` must start with `/`. */
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  try {
    return await portalApi<T>(joinApiPath(path), { signal });
  } catch (err) {
    rethrow(err);
  }
}

/** POST `/api/portal/<path>` with a JSON body. */
export async function apiPost<T, B = unknown>(
  path: string,
  body: B,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await portalApi<T>(joinApiPath(path), { method: 'POST', body, signal });
  } catch (err) {
    rethrow(err);
  }
}

/** PATCH `/api/portal/<path>` with a JSON body. */
export async function apiPatch<T, B = unknown>(
  path: string,
  body: B,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await portalApi<T>(joinApiPath(path), { method: 'PATCH', body, signal });
  } catch (err) {
    rethrow(err);
  }
}

/** DELETE `/api/portal/<path>`. Returns void (the BFF responds 204). */
export async function apiDelete(path: string, signal?: AbortSignal): Promise<void> {
  try {
    await portalApi<void>(joinApiPath(path), { method: 'DELETE', signal });
  } catch (err) {
    rethrow(err);
  }
}

/** Accept a caller path with or without a leading slash and produce a
 *  full BFF URL. Callers should pass relative-to-portal-root paths like
 *  `/me` or `/vehicles` (matching PSM portal/ convention). */
function joinApiPath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized.startsWith('/api/portal/')) return normalized;
  return `/api/portal${normalized}`;
}

export { PortalApiError } from './apiClient';
